import type { BoxProps } from "@chakra-ui/react";
import { Box, useColorMode } from "@chakra-ui/react";
import { useEffect, useRef } from "react";

export type DitherVariant = "granular" | "snake";

export interface DitherGradientProps extends Omit<BoxProps, "color"> {
  /**
   * Visual effect.
   * - `'granular'` — full-screen dithered field of interfering, domain-warped waves.
   * - `'snake'` — dither forms a symmetric ribbon around an undulating
   *   crest line, fading to solid `color1` on both sides.
   * @default 'snake'
   */
  variant?: DitherVariant;
  /**
   * Chakra color token for the first color of the dithered gradient
   * (e.g. `'bg.container'`, `'fg.default'`). Resolved at runtime so
   * dark mode swaps colors automatically.
   * @default 'bg.container'
   */
  color1?: string;
  /**
   * Chakra color token for the second color of the dithered gradient.
   * @default 'neutral.subtle'
   */
  color2?: string;
  /**
   * Animation speed multiplier. `0` produces a static dither, `1` is the
   * default cadence, higher values move faster.
   * @default 1
   */
  speed?: number;
  /**
   * Logical pixel size for the dither pattern. `1` = native (sharpest),
   * higher values are blockier (cheaper to render and a more retro look).
   * @default 1.5
   */
  pixelSize?: number;
  /**
   * Width of the dither band around the snake crest, as a fraction of
   * viewport height. Only used by `variant="snake"`.
   * @default 0.68
   */
  bandWidth?: number;
  /**
   * Peak dither density at the snake crest. Only used by `variant="snake"`.
   * @default 0.61
   */
  intensity?: number;
  /**
   * Vertical position of the snake crest, as a fraction of viewport
   * height. `0` = top, `1` = bottom. Only used by `variant="snake"`.
   * @default 0.86
   */
  verticalAlignment?: number;
  /**
   * Diagonal tilt of the snake crest. `0` = horizontal. Only used by
   * `variant="snake"`.
   * @default 0
   */
  diagonalAlignment?: number;
  /**
   * Amplitude multiplier for the snake undulation. Only used by
   * `variant="snake"`.
   * @default 0.8
   */
  curve?: number;
}

const VERT = `attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const FRAG = `precision mediump float;
uniform vec2  u_resolution;
uniform float u_time;
uniform vec3  u_color1;
uniform vec3  u_color2;
uniform int   u_variant;
uniform float u_bandWidth;
uniform float u_intensity;
uniform float u_verticalAlignment;
uniform float u_diagonalAlignment;
uniform float u_curve;

float bayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }
#define bayer4(a) (bayer2(0.5 * (a)) * 0.25 + bayer2(a))
#define bayer8(a) (bayer4(0.5 * (a)) * 0.25 + bayer2(a))

float granularField(vec2 uv, float t) {
  vec2 p = uv + 0.18 * vec2(
    sin(uv.y * 2.6 + t * 0.7),
    cos(uv.x * 2.2 - t * 0.5 + 1.3)
  );
  return 0.5
    + 0.32 * sin(p.x * 2.4 + p.y * 1.3 + t)
    + 0.22 * sin(p.x * 1.1 - p.y * 3.1 + t * 0.85 + 1.7);
}

float snakeField(vec2 uv, vec2 uvRaw, float t) {
  float lineY = (1.0 - u_verticalAlignment)
    + (uv.x - 0.5 * (u_resolution.x / u_resolution.y)) * u_diagonalAlignment
    + u_curve * 0.12 * sin(uv.x * 3.0 + t * 1.2)
    + u_curve * 0.04 * sin(uv.x * 7.0 - t * 0.9 + 1.7);
  float dist = abs(lineY - uvRaw.y);
  return u_intensity * (1.0 - smoothstep(0.0, max(u_bandWidth, 0.0001), dist));
}

void main() {
  vec2 uvRaw = gl_FragCoord.xy / u_resolution.xy;
  vec2 uv = uvRaw;
  uv.x *= u_resolution.x / u_resolution.y;

  float t = u_time * 0.35;

  float g = (u_variant == 1)
    ? snakeField(uv, uvRaw, t)
    : granularField(uv, t);

  float d = bayer8(gl_FragCoord.xy) - 0.5;
  float v = step(0.5, g + d);
  gl_FragColor = vec4(mix(u_color1, u_color2, v), 1.0);
}`;

const parseCssColor = (value: string): [number, number, number] => {
  const match = value.match(/rgba?\(([^)]+)\)/);
  if (!match) return [0, 0, 0];
  const parts = match[1].split(",").map((p) => parseFloat(p.trim()));
  return [(parts[0] ?? 0) / 255, (parts[1] ?? 0) / 255, (parts[2] ?? 0) / 255];
};

const compile = (gl: WebGLRenderingContext, type: number, src: string) => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

export const DitherGradient = ({
  variant = "snake",
  color1 = "bg.container",
  color2 = "neutral.subtle",
  speed = 1,
  pixelSize = 1.5,
  bandWidth = 0.68,
  intensity = 0.61,
  verticalAlignment = 0.86,
  diagonalAlignment = 0,
  curve = 0.8,
  ...rest
}: DitherGradientProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const probe1Ref = useRef<HTMLDivElement>(null);
  const probe2Ref = useRef<HTMLDivElement>(null);
  const { colorMode } = useColorMode();
  // Under reduced motion the canvas paints once and never re-arms its frame
  // loop, so a color-mode change has to ask for a repaint explicitly.
  const redrawRef = useRef<() => void>(() => {});

  const propsRef = useRef({
    variant,
    speed,
    pixelSize,
    bandWidth,
    intensity,
    verticalAlignment,
    diagonalAlignment,
    curve,
    rgb1: [0, 0, 0] as [number, number, number],
    rgb2: [0, 0, 0] as [number, number, number],
  });

  useEffect(() => {
    propsRef.current.variant = variant;
    propsRef.current.speed = speed;
    propsRef.current.pixelSize = pixelSize;
    propsRef.current.bandWidth = bandWidth;
    propsRef.current.intensity = intensity;
    propsRef.current.verticalAlignment = verticalAlignment;
    propsRef.current.diagonalAlignment = diagonalAlignment;
    propsRef.current.curve = curve;
  });

  // Read once now so the first paint has real colors, and again a frame later:
  // Chakra flips the semantic-token CSS variables from a parent effect that
  // runs after this one, so on a color-mode change the immediate read still
  // samples the outgoing theme.
  useEffect(() => {
    const readProbes = () => {
      if (!probe1Ref.current || !probe2Ref.current) return;
      propsRef.current.rgb1 = parseCssColor(
        getComputedStyle(probe1Ref.current).color,
      );
      propsRef.current.rgb2 = parseCssColor(
        getComputedStyle(probe2Ref.current).color,
      );
    };
    readProbes();
    const raf = requestAnimationFrame(() => {
      readProbes();
      redrawRef.current();
    });
    return () => cancelAnimationFrame(raf);
  }, [color1, color2, colorMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", {
      antialias: false,
      premultipliedAlpha: false,
    });
    if (!gl) return;

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );

    const aPosition = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

    const uResolution = gl.getUniformLocation(program, "u_resolution");
    const uTime = gl.getUniformLocation(program, "u_time");
    const uColor1 = gl.getUniformLocation(program, "u_color1");
    const uColor2 = gl.getUniformLocation(program, "u_color2");
    const uVariant = gl.getUniformLocation(program, "u_variant");
    const uBandWidth = gl.getUniformLocation(program, "u_bandWidth");
    const uIntensity = gl.getUniformLocation(program, "u_intensity");
    const uVerticalAlignment = gl.getUniformLocation(
      program,
      "u_verticalAlignment",
    );
    const uDiagonalAlignment = gl.getUniformLocation(
      program,
      "u_diagonalAlignment",
    );
    const uCurve = gl.getUniformLocation(program, "u_curve");

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let visible = true;
    let inView = true;
    let raf = 0;
    let startTs = performance.now();

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const px = Math.max(1, propsRef.current.pixelSize);
      const w = Math.max(1, Math.floor(rect.width / px));
      const h = Math.max(1, Math.floor(rect.height / px));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const draw = (ts: number) => {
      resize();
      const p = propsRef.current;
      gl.uniform2f(uResolution, canvas.width, canvas.height);
      gl.uniform1f(
        uTime,
        reducedMotion ? 0 : ((ts - startTs) / 1000) * p.speed,
      );
      gl.uniform1i(uVariant, p.variant === "snake" ? 1 : 0);
      gl.uniform1f(uBandWidth, p.bandWidth);
      gl.uniform1f(uIntensity, p.intensity);
      gl.uniform1f(uVerticalAlignment, p.verticalAlignment);
      gl.uniform1f(uDiagonalAlignment, p.diagonalAlignment);
      gl.uniform1f(uCurve, p.curve);
      gl.uniform3f(uColor1, p.rgb1[0], p.rgb1[1], p.rgb1[2]);
      gl.uniform3f(uColor2, p.rgb2[0], p.rgb2[1], p.rgb2[2]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const tick = (ts: number) => {
      draw(ts);
      // A single frame under reduced motion; clear the id so later one-off
      // redraws (resize, color-mode change) are not mistaken for a live loop.
      if (reducedMotion) {
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    const start = () => {
      if (raf || !visible || !inView) return;
      startTs = performance.now();
      raf = requestAnimationFrame(tick);
    };

    const stop = () => {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const onVisibility = () => {
      visible = !document.hidden;
      if (visible) start();
      else stop();
    };

    const io = new IntersectionObserver(
      (entries) => {
        inView = entries[0]?.isIntersecting ?? true;
        if (inView) start();
        else stop();
      },
      { threshold: 0 },
    );
    io.observe(canvas);

    const ro = new ResizeObserver(() => {
      if (raf) return;
      draw(performance.now());
    });
    ro.observe(canvas);

    document.addEventListener("visibilitychange", onVisibility);
    redrawRef.current = () => {
      if (raf) return;
      draw(performance.now());
    };

    draw(performance.now());
    if (!reducedMotion) start();

    return () => {
      redrawRef.current = () => {};
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, []);

  return (
    <Box position="relative" overflow="hidden" {...rest}>
      <Box
        ref={probe1Ref}
        color={color1}
        position="absolute"
        opacity="0"
        pointerEvents="none"
        aria-hidden
      />
      <Box
        ref={probe2Ref}
        color={color2}
        position="absolute"
        opacity="0"
        pointerEvents="none"
        aria-hidden
      />
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          imageRendering: "pixelated",
        }}
      />
    </Box>
  );
};
